import type { Doc } from "building-types/doc";
import type { Formula } from "building-types/options/formula";
import type { OptionChoices } from "building-types/options/optionChoices";
import type { OptionDefinitions } from "building-types/options/optionDefinitions";
import type { DocOptionLock, DocOptionSet } from "building-types/options/optionSet";
import type { OptionSetEntityDisconnectedTable } from "building-types/options/optionSetEntityRelationsTypes";
import type { DocOptionSetOverrides } from "building-types/options/optionTemplateTypes";
import type { RWTable, Table } from "common/data/table";
import objectFilter from "common/utils/objectFilter";
import objectIsEmpty from "common/utils/objectIsEmpty";
import { sortTableBySortIndex } from "common/utils/sortIndex";
import type { Mutable } from "common/utils/types";
import {
  docGetActiveOptionTemplate,
  docGetActiveOptionTemplateOverrides,
  docGetOptionTemplateOverridesByOptionSetId,
} from "doc/docGetOptionTemplates";
import { parseFormula } from "doc/formula/formulaParsing";
import { docToOptionDefinitions } from "doc/options/docGetInstanceOptionSets";
import { groupOptionEffectsByRuleId } from "doc/options/optionTools";
import type { Value } from "expr-eval";
import { findFirstEnabledOptionId } from "./convertOptionSet";
import type { DocOptionStateView } from "./docOptionStateView";
import { buildModifyTargetRowPreview, previewModifyTargetRows } from "./intersecting/modifyTargetRowPreview";
import { mergeOptionTemplatesIntoDefinitions } from "./mergeOptionTemplatesIntoOptionSets";
import { isIntersectingOptionSet } from "./optionChoicesTools";
import { getOptionChoicesWithDefaults } from "./optionChoicesTools";
import { getDisconnectConditionOptions, getOptionLockByOptionSetId } from "./optionLocksTools";
import type { OptionRuleReferencesByOption } from "./optionRule";
import { getRulesByOptions } from "./optionRule";
import { optionSetDefaultOptionId } from "./optionSetBaseUtils";
import type { OptionSetState, OptionSetStateSet, RWOptionSetStateSet } from "./optionSetState";
import {
  optionSetDisabledOptionIds,
  optionSetHasNoEnabledOptions,
  optionSetRecordsNoDecision,
  optionSetSelectedOptionId,
} from "./optionSetTools";
import { PaletteTags } from "./paletteOptionSet";

export function optionSetStateNew(
  uniqueId: string,
  currentValue: string,
  previousValue: string,
  optionSet: DocOptionSet,
  disconnectedOptions?: { [id: string]: boolean },
  disconnectConditionOptions?: Table<Formula>,
  optionRuleReferences?: OptionRuleReferencesByOption,
  lockedOptions: { [id: string]: boolean } = {},
): OptionSetState {
  const state: OptionSetState = {
    id: uniqueId,
    currentValue,
    previousValue,
    optionSet,
    optionRuleReferences,
    lockedOptions,
  };

  if (disconnectedOptions && !objectIsEmpty(disconnectedOptions)) {
    state.disconnectedOptions = disconnectedOptions;
    state.disconnectConditionOptions = disconnectConditionOptions;
  }
  return state;
}

export interface OptionSetChangeContext {
  readonly optionSetId: string;
  readonly optionSet: DocOptionSet;
}

export interface OptionSetStateFromBuildingParams {
  /**
   * When true, includes sets that don't record a decision {@link optionSetRecordsNoDecision}.
   * When false, excludes them (save, Studio, pricing). Required — no default.
   *
   * Display-only state does not write optionChoices; sanitize drops readonly and default matches before save.
   */
  includeNoDecisionSets: boolean;
  /**
   * TODO: Adding disconnectedOptionSets as an optional parameter for now, but it should be a required check in this function.
   * Because running evalOptionSetEntityRelations to generate disconnectedOptionSets duplicates a lot of work here,
   * (which is also duplicated in specific implementations, reducers, etc), this should be fully refactored to become
   * the single source of truth for determining optionSet states (likewise with optionSetStateFromDoc).
   */
  disconnectedOptionSets?: OptionSetEntityDisconnectedTable;
  /**
   * In most cases, docOptionOverrides will be merged in upstream. In which case, they would already be present on doc.optionSets.
   * Merging them in again breaks the inheritance stack, by overriding downstream overrides (eg. from a master / location plan).
   * This behavior can still be enabled when evaluating a doc.
   * TODO: re-evaluate whether mergeDocOptionSetOverrides is still needed as part of a larger option state pipeline refactor.
   */
  mergeDocOptionSetOverrides?: boolean;
}

export function optionSetStateSetFromBuilding(
  building: DocOptionStateView,
  params: OptionSetStateFromBuildingParams,
): OptionSetStateSet {
  const { includeNoDecisionSets, disconnectedOptionSets, mergeDocOptionSetOverrides = false } = params;
  const stateById: RWOptionSetStateSet = {};

  if (!building) {
    return stateById;
  }

  const definitions = mergeDocOptionSetOverrides
    ? mergeStudioDocOptionTemplateIntoOptionDefs(building)
    : docToOptionDefinitions(building.doc);
  const optionChoices = getOptionChoicesWithDefaults(definitions, building.optionChoices);

  const id = mergeDocOptionSetOverrides ? docGetActiveOptionTemplate(building.doc)?.id : null;
  const optionSetOverrides = id
    ? docGetOptionTemplateOverridesByOptionSetId(building.doc, { type: "OPTION_TEMPLATE", id })
    : {};

  // Baseline option sets for everything below: filtering drops intersecting sets, sets with every option disabled,
  // and palette settings sets that aren't active for this building.
  //
  // Don't filter single-option sets out here, may be needed for rules in getRulesByOptions. We may filter them out later if needed.
  const filteredOptionSets = filterOptionSets({ optionSets: definitions.optionSets, building });

  const { optionRules, optionEffects, optionLocks } = building.doc;
  const rulesByOptions = getRulesByOptions(optionRules, building.doc.optionEffects, filteredOptionSets);
  const optionsLockedByRules: RWTable<RWTable<boolean>> = {};
  const optionEffectsByRuleId = groupOptionEffectsByRuleId(optionEffects);

  // make a list of options that are locked because of a non-overridable rule
  for (const rule of sortTableBySortIndex(optionRules ?? {})) {
    if (rule.overridable) {
      continue;
    }

    try {
      // formulas are inverted so if we pass we should skip
      if (parseFormula(rule.predicate.formula).evaluate(optionChoices as Value)) {
        continue;
      }
    } catch (e) {
      continue;
    }

    const ruleOptionEffects = optionEffectsByRuleId[rule.id];
    if (ruleOptionEffects === undefined) {
      continue;
    }
    for (const effect of ruleOptionEffects) {
      if (effect.effectType === "ENABLE" && effect.optionId) {
        optionsLockedByRules[effect.optionSetId] = {
          ...optionsLockedByRules[effect.optionSetId],
          [effect.optionId]: true,
        };
      }
    }
  }

  // Now deal with intersecting option modifications

  // Performance optimization: precompute intersecting MODIFY effects per selectable option.
  // Each linked combo row needs N conditions to match (e.g. fridge=basic AND sink=premium); a
  // "match count" per row tracks how many of its conditions current choices satisfy — the row
  // matches when that count reaches N. Match-once combos (conditions mention other sets only) are
  // resolved once at current choices; per-row combos (conditions include the target set) keep
  // those counts and, when previewing each target row, adjust only that set's contribution (+1 for
  // the previewed option, −1 for the current one) instead of rescanning every combo row.
  const evaluatedOptionSets = building.evaluatedOptionSets ?? building.doc.optionSets;
  const intersectionDefinitions: OptionDefinitions = {
    optionSets: evaluatedOptionSets,
    instanceOptionSets: definitions.instanceOptionSets,
  };
  const modifyTargetRowPreview = buildModifyTargetRowPreview(intersectionDefinitions, optionChoices);

  // Iterate through filtered options sets, build disconnected options and push onto
  // stateById table
  for (const [optionSetId, optionSet] of Object.entries(filteredOptionSets)) {
    if (!includeNoDecisionSets && optionSetRecordsNoDecision(optionSet)) {
      continue;
    }

    const { value, disconnectedOptions, conditions } = filterOptionChoiceValue({
      definitions,
      optionLocks,
      optionSet,
      optionChoices,
      optionSetOverrides,
      optionSetId,
      disconnectedOptionSets,
    });

    let finalOptionSet = optionSet;

    // Apply intersecting modifications if this option set is targeted by any MODIFY intersection
    if (modifyTargetRowPreview?.modifyTargetOptionSetIds.has(optionSetId)) {
      const clonedOptions = previewModifyTargetRows(modifyTargetRowPreview, optionSetId, optionSet);
      if (clonedOptions) {
        finalOptionSet = {
          ...optionSet,
          options: clonedOptions,
        };
      }
    }

    stateById[optionSetId] = optionSetStateNew(
      optionSetId,
      value ?? "",
      optionChoices[optionSetId] ?? "",
      finalOptionSet,
      disconnectedOptions,
      conditions,
      rulesByOptions[optionSetId],
      optionsLockedByRules[optionSetId] ?? {},
    );
  }

  return stateById;
}

export function mergeStudioDocOptionTemplateIntoOptionDefs(
  building: DocOptionStateView,
  filterDisabledOptions?: boolean,
) {
  const overrides = docGetActiveOptionTemplateOverrides(building?.doc);
  const initialDefinitions = docToOptionDefinitions(building.doc);
  const definitions = mergeOptionTemplatesIntoDefinitions(initialDefinitions, [overrides]);

  if (!filterDisabledOptions) {
    return definitions;
  } else {
    const optionSetsWithEnabledOptions: RWTable<DocOptionSet> = {};

    for (const id in definitions.optionSets) {
      const optionSet = definitions.optionSets[id];
      const disabledValues = optionSetDisabledOptionIds(optionSet);

      optionSetsWithEnabledOptions[id] = {
        ...optionSet,
        options: objectFilter(optionSet.options, (option) => !disabledValues.includes(String(option.id))),
      };
    }

    return {
      ...definitions,
      optionSets: optionSetsWithEnabledOptions,
    };
  }
}

interface FilterOptionSetsOptions {
  readonly optionSets: Doc["optionSets"];
  readonly building?: DocOptionStateView;
}

/**
 * Filters intersecting sets, all-disabled sets, inactive palette settings sets.
 * Handling for sets with single-option is separate: see {@link optionSetRecordsNoDecision} and `includeNoDecisionSets`.
 */
function filterOptionSets({ optionSets, building }: FilterOptionSetsOptions): Doc["optionSets"] {
  const filteredOptionSets: RWTable<DocOptionSet> = {};
  for (const [optionSetId, optionSet] of Object.entries(optionSets)) {
    // Intersecting option sets can't be chosen
    if (isIntersectingOptionSet(optionSet)) {
      continue;
    }

    if (optionSetHasNoEnabledOptions(optionSet)) {
      continue;
    }

    // Only include palette option sets that are active for a specific elevation
    if (building && optionSet?.tags?.includes(PaletteTags.SETTINGS_OPTION_SET)) {
      const { interiorMaterials, exteriorMaterials } = building.settings;

      if (!interiorMaterials.id || !exteriorMaterials.id) {
        continue;
      }

      if (!optionSetId.includes(exteriorMaterials.id) && !optionSetId.includes(interiorMaterials.id)) {
        continue;
      }
    }

    filteredOptionSets[optionSetId] = optionSet;
  }

  return filteredOptionSets;
}

interface FilterOptionChoiceValueOptions {
  readonly definitions: OptionDefinitions;
  readonly optionLocks: Table<DocOptionLock>;
  readonly optionSet: DocOptionSet;
  readonly optionChoices: OptionChoices;
  readonly optionSetOverrides?: Table<DocOptionSetOverrides>;
  readonly optionSetId: string;
  readonly disconnectedOptionSets?: OptionSetEntityDisconnectedTable;
}

interface FilterOptionChoiceValueResult {
  readonly value: string | null;
  readonly disconnectedOptions: Table<boolean>;
  readonly conditions: Table<Formula>;
}

export function filterOptionChoiceValue({
  definitions,
  optionLocks,
  optionSet,
  optionChoices,
  optionSetOverrides,
  optionSetId,
  disconnectedOptionSets,
}: FilterOptionChoiceValueOptions): FilterOptionChoiceValueResult {
  const disconnectedOptions: RWTable<boolean> = {};
  const optionLock = getOptionLockByOptionSetId(optionLocks, optionSetId);

  const conditions = getDisconnectConditionOptions(optionLock, optionSetOverrides?.[optionSetId]);

  for (const [optionId, { formula }] of Object.entries(conditions)) {
    try {
      if (!parseFormula(formula).evaluate(optionChoices as Value)) {
        disconnectedOptions[optionId] = true;
      }
    } catch (e) {
      // console.warn(`Error evaluating ${condition.formula} - ${e}`);
    }
  }

  let currentValue = optionSetSelectedOptionId(definitions, optionChoices, optionSetId);

  // Reset current value if option is disconnected or disabled
  const isDisconnected = (optionId: string) =>
    disconnectedOptions?.[optionId] || optionSet?.options?.[optionId]?.disabled;

  if (
    (currentValue !== null && isDisconnected(currentValue)) ||
    (disconnectedOptionSets && disconnectedOptionSets[optionSet.id])
  ) {
    const defaultValue = optionSetDefaultOptionId(optionSet, definitions.optionSets);
    currentValue = defaultValue;
    // Check if default value is also disconnected and find first enabled option
    if (defaultValue && isDisconnected(defaultValue)) {
      const enabledOptionId = findFirstEnabledOptionId(optionSet, definitions.optionSets, (id) => {
        return disconnectedOptions[id] === true;
      });
      if (enabledOptionId) {
        currentValue = enabledOptionId;
      } // TODO: Is there ever a case where there are no enabled options?
    }
  }

  return {
    value: currentValue,
    disconnectedOptions,
    conditions,
  };
}

/**
 * Lightweight choice-mutation resolver for the pricing path.
 * Runs filterOptionChoiceValue per set without building full OptionSetState
 * (no rules / intersecting MODIFY preview).
 */
export function resolveOptionChoiceMutations(
  building: DocOptionStateView,
  disconnectedOptionSets: OptionSetEntityDisconnectedTable,
  setToDefault: OptionChoices = {},
): OptionChoices {
  const newOptionChoices: Mutable<OptionChoices> = { ...setToDefault };

  if (!building) {
    return newOptionChoices;
  }

  const definitions = docToOptionDefinitions(building.doc);
  const optionChoices = getOptionChoicesWithDefaults(definitions, building.optionChoices);
  const filteredOptionSets = filterOptionSets({ optionSets: definitions.optionSets, building });
  const { optionLocks } = building.doc;

  for (const [optionSetId, optionSet] of Object.entries(filteredOptionSets)) {
    if (optionSetRecordsNoDecision(optionSet)) {
      continue;
    }

    const { value } = filterOptionChoiceValue({
      definitions,
      optionLocks,
      optionSet,
      optionChoices,
      optionSetId,
      disconnectedOptionSets,
    });

    const currentValue = value ?? "";
    const previousValue = optionChoices[optionSetId] ?? "";

    if (!optionSet.disabled && !disconnectedOptionSets[optionSet.id] && currentValue !== previousValue) {
      newOptionChoices[optionSet.id] = currentValue;
    }
  }

  return newOptionChoices;
}
